/**
 * AttentionBand — "Needs your attention" (band 1 of the CM home; brief
 * of record 2026-07-06).
 *
 * Every item is DERIVED from the pipeline states of the CM's OWN notes —
 * there is NO create-task affordance, ever (protected ruling). Ordering
 * is R4a (severity class, max-member bundling, oldest-first within
 * class) via src/lib/cmHome.ts; the badge counts CARDS; cap 5 with the
 * overflow grouped by client.
 *
 * Affordances per R1: Review / Fix note / Retry are SECONDARY buttons
 * (bordered task-destinations); the count badge is the gold CIRCLE
 * (CountBadge — DL5's one badge shape); the kickback badge is the "!"
 * glyph, never a number (the gold circle means "count of updates").
 */

import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import type { CmHomeAttentionWire, CmHomeSpiceAttentionWire } from '@/lib/cmHomeApi';
import { insufficientContentMessage } from '@/lib/kickbackCopy';
import { writeSpiceVisitPrefill } from '@/lib/spiceVisitPrefill';
import { CountBadge } from '@/components/primitives/CountBadge';
import { MY_CLIENTS_HEADINGS } from '@/lib/headingCase';
import { QuietSection, ROW_CAP, ROW_LIST, SECTION_CARD, ShowAllToggle } from '@/components/home/RailParts';
import {
  attentionCardTitle,
  orderAttention,
  spiceAttentionTitle,
  typeSummary,
  proseDay,
} from '@/lib/cmHome';

function cardMeta(card: CmHomeAttentionWire, todayIso: string): string {
  if (card.kind === 'needs_detail') {
    // Shared kickback copy (founder feedback 2026-07-06): "support"/"Add
    // detail". Every needs_detail card is a stored kickback; at zero hours
    // (or none entered) the copy reads the neutral no-line sentence.
    return insufficientContentMessage(Number(card.hours), true);
  }
  if (card.kind === 'extraction_failed') {
    return "It couldn't be turned into record updates. Open the note to try again.";
  }
  const source = card.note_kind === 'assessment' ? 'the assessment you added' : 'your visit note';
  return `From ${source} ${proseDay(card.note_occurred_on, todayIso)}: ${typeSummary(card.type_counts ?? {})}`;
}

/** A card on a client outside her scope: who acts on it instead. */
function outOfScopeLine(card: CmHomeAttentionWire): string {
  const ask = card.kind === 'confirm_updates' ? 'confirm or dismiss these updates' : 'try this note again';
  return `${card.client_display_name} is no longer on your clients. Ask the office to ${ask}`;
}

export interface AttentionBandProps {
  attention: readonly CmHomeAttentionWire[];
  /** A SPICE-only viewer's missed-note items — always empty for a care
   *  manager (the wire's own gate, cmHome.ts). */
  spiceAttention?: readonly CmHomeSpiceAttentionWire[];
  todayIso: string;
  tenantSlug: string;
  /** The missed-note item's door is a write-intent action (opens the
   *  Email helper to write the missing session note) — gated the same
   *  as every other note door in this feature (ClientsBand/MyWeekBand's
   *  own canWriteClinical). */
  canWriteClinical?: boolean;
}

export function AttentionBand({
  attention,
  spiceAttention = [],
  todayIso,
  tenantSlug,
  canWriteClinical = false,
}: AttentionBandProps): ReactNode {
  const navigate = useNavigate();
  const ordered = orderAttention(attention);
  const totalCount = ordered.length + spiceAttention.length;
  // LP-24: the first ROW_CAP items (SPICE first, as they render), then
  // "Show all N"; the heading's chip always counts every item.
  const [showAll, setShowAll] = useState(false);
  const spiceShown = showAll ? spiceAttention : spiceAttention.slice(0, ROW_CAP);
  const cardsShown = showAll ? ordered : ordered.slice(0, Math.max(0, ROW_CAP - spiceShown.length));

  function openNote(card: CmHomeAttentionWire): void {
    // A kickback on a client she can no longer open: her own line is still
    // hers to fix, on My time, in the note's period with its line open.
    if (card.kind === 'needs_detail' && card.out_of_scope === true) {
      const q = new URLSearchParams({ period: card.note_occurred_on, fix_note: card.note_id });
      navigate(`/t/${tenantSlug}/my-time?${q.toString()}`);
      return;
    }
    // The record page's Notes tab, anchored to the note (ProvenanceChip
    // deep-link pattern). Confirm cards land beside the confirm queue.
    navigate(`/t/${tenantSlug}/clients/${card.client_id}?tab=notes#note-${card.note_id}`);
  }

  // The SAME door My week uses — the client and its last plan carry
  // into the Email helper (spiceVisitPrefill.ts), no last-plan re-read.
  function openSpiceItem(item: CmHomeSpiceAttentionWire): void {
    writeSpiceVisitPrefill(tenantSlug, item.client_id, {
      client_display_name: item.client_display_name,
      last_plan: item.last_plan,
      contact_first_name: null,
    });
    navigate(`/t/${tenantSlug}/ai-tools/email?client_id=${item.client_id}`);
  }

  if (totalCount === 0) {
    // Emptiness is quiet: the title and one line, no card.
    return (
      <section aria-labelledby="attention-heading" data-testid="attention-band">
        <QuietSection headingId="attention-heading" title={MY_CLIENTS_HEADINGS.notesToConfirm}>
          <p data-testid="attention-empty" className="text-title-sm text-ink-soft">
            Nothing needs you right now.
          </p>
        </QuietSection>
      </section>
    );
  }

  return (
    <section aria-labelledby="attention-heading" data-testid="attention-band" className={SECTION_CARD}>
      {/* LP-11: the My Clients rail's Notes To Confirm. Every record item,
          never filtered away: five show, then "Show all N" (LP-24); hairline
          rows. */}
      <div className="mb-2 flex h-7 items-center gap-2.5">
        <h2 id="attention-heading" className="flex h-7 items-center font-display text-lg leading-7 text-sage-800">
          {MY_CLIENTS_HEADINGS.notesToConfirm}
        </h2>
        <Chip variant="caution" className="tabular-nums" data-testid="attention-count">
          {totalCount} {totalCount === 1 ? 'item' : 'items'}
        </Chip>
      </div>
        <ul className={`m-0 list-none p-0 ${ROW_LIST}`}>
          {spiceShown.map((item) => (
            <li key={`${item.client_id}:${item.visit_on}`} data-testid="spice-attention-card" className="flex items-center gap-3 py-2.5">
              <CountBadge aria-hidden="true" size="sm" variant="pepper" data-testid="spice-attention-badge">
                !
              </CountBadge>
              <p className="min-w-0 flex-1 truncate text-title-sm font-semibold text-ink" title={spiceAttentionTitle(item, todayIso)}>
                {spiceAttentionTitle(item, todayIso)}
              </p>
              {canWriteClinical && (
                <Button variant="ghost" size="sm" onClick={() => openSpiceItem(item)}>
                  Write the SPICE email summary
                </Button>
              )}
            </li>
          ))}
          {cardsShown.map((card) => {
            // Her own note on a client she can no longer open: the row's
            // door would refuse her, so it names who acts instead.
            const away = card.out_of_scope === true && card.kind !== 'needs_detail';
            const kick = card.kind !== 'confirm_updates';
            return (
              <li key={card.note_id + card.kind} data-testid="attention-card" data-kind={card.kind} className="flex items-center gap-3 py-2.5">
                {/* DL5: the one badge shape, small in the rail: the gold circle
                    counts a confirm's updates; the pepper "!" marks a kickback. */}
                <CountBadge aria-hidden="true" size="sm" variant={kick ? 'pepper' : 'gold'} data-testid="attention-card-badge">
                  {kick ? '!' : (card.pending_count ?? 0)}
                </CountBadge>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-title-sm font-semibold text-ink" title={attentionCardTitle(card, todayIso)}>
                    {attentionCardTitle(card, todayIso)}
                  </p>
                  {away ? (
                    <p data-testid="cm-home-attention-out-of-scope" className="mt-0.5 text-xs text-ink-soft">
                      {outOfScopeLine(card)}
                    </p>
                  ) : (
                    <p className="mt-0.5 truncate text-xs text-ink-soft">{cardMeta(card, todayIso)}</p>
                  )}
                </div>
                {!away && (
                  <Button variant="ghost" size="sm" onClick={() => openNote(card)}>
                    {card.kind === 'confirm_updates' ? 'Review' : card.kind === 'needs_detail' ? 'Fix note' : 'Retry'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      <ShowAllToggle total={totalCount} open={showAll} onToggle={() => setShowAll((v) => !v)} testId="attention-show-all" />
    </section>
  );
}
